import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@examdojo/core/environment';
import { map, Observable, of } from 'rxjs';

interface TokenExchangeRequest {
  audience: string;
  grantType: string;
  requestedTokenType: string;
  scope: string;
  subjectTokenType: string;
  subjectToken: string;
}

interface TokenExchangeResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface CachedToken {
  accessToken: string;
  expiresAt: number;
}

@Injectable()
export class GoogleCloudTokenExchangeService {
  constructor(private readonly http: HttpClient) {}

  private readonly tokenCache = new Map<string, CachedToken>();
  private readonly stsUrl = 'https://sts.googleapis.com/v1/token';
  private readonly audience = `//iam.googleapis.com/projects/${environment.bigQuery.authGcpProjectId}/locations/global/workloadIdentityPools/supabase/providers/supabase`;

  exchangeToken(supabaseToken: string): Observable<string> {
    const cachedToken = this.tokenCache.get(supabaseToken);
    if (cachedToken && this.isTokenValid(cachedToken)) {
      return of(cachedToken.accessToken);
    }

    const request: TokenExchangeRequest = {
      audience: this.audience,
      grantType: 'urn:ietf:params:oauth:grant-type:token-exchange',
      requestedTokenType: 'urn:ietf:params:oauth:token-type:access_token',
      scope: 'https://www.googleapis.com/auth/cloud-platform',
      subjectTokenType: 'urn:ietf:params:oauth:token-type:jwt',
      subjectToken: supabaseToken,
    };

    return this.http.post<TokenExchangeResponse>(this.stsUrl, request).pipe(
      map((response) => {
        const expiresAt = Date.now() + (response.expires_in - 60) * 1000; // Subtract 60 seconds for safety margin
        this.tokenCache.set(supabaseToken, {
          accessToken: response.access_token,
          expiresAt,
        });

        return response.access_token;
      }),
    );
  }

  private isTokenValid(cachedToken: CachedToken): boolean {
    return Date.now() < cachedToken.expiresAt;
  }
}
